export type Platform = 'instagram' | 'tiktok' | 'youtube';
export type ContentType = 'post' | 'reel' | 'story' | 'video' | 'short';
export type Niche =
  | 'fashion' |'beauty' |'fitness' |'food' |'travel' |'tech' |'finance' |'gaming' |'lifestyle' |'parenting' |'education' |'music' |'sports' |'business' |'health';

export interface RateInput {
  platform: Platform;
  followers: number;
  engagementRate: number; // percentage, e.g. 3.5
  contentType: ContentType;
  niche: Niche;
  usageRights: boolean;
  exclusivity: boolean;
}

export interface RateBreakdown {
  baseCPM: number;
  baseRate: number;
  engagementMultiplier: number;
  nichePremium: number;
  contentTypeMultiplier: number;
  usageRightsAddon: number;
  exclusivityAddon: number;
  finalRate: number;
  suggestedLow: number;
  suggestedMid: number;
  suggestedHigh: number;
  audienceTier: string;
  engagementStatus: 'excellent' | 'good' | 'average' | 'below-average';
  estimatedReach: number;
}

// CPM benchmarks per platform (USD per 1000 followers baseline)
const PLATFORM_CPM: Record<Platform, number> = {
  instagram: 8,
  tiktok: 5,
  youtube: 18,
};

// Platform average engagement rates
const PLATFORM_AVG_ENGAGEMENT: Record<Platform, number> = {
  instagram: 3.5,
  tiktok: 5.5,
  youtube: 4.0,
};

// Content type multipliers
const CONTENT_TYPE_MULTIPLIERS: Record<ContentType, number> = {
  video: 2.5,
  reel: 1.8,
  post: 1.0,
  story: 0.4,
  short: 1.4,
};

// Niche premium percentages
const NICHE_PREMIUMS: Record<Niche, number> = {
  finance: 0.45,
  tech: 0.40,
  business: 0.35,
  health: 0.30,
  beauty: 0.25,
  fitness: 0.20,
  travel: 0.20,
  fashion: 0.18,
  education: 0.15,
  food: 0.12,
  sports: 0.12,
  lifestyle: 0.08,
  gaming: 0.10,
  parenting: 0.08,
  music: 0.06,
};

// Audience tier labels
function getAudienceTier(followers: number): string {
  if (followers < 10000) return 'Nano Creator';
  if (followers < 50000) return 'Micro Creator';
  if (followers < 250000) return 'Mid-Tier Creator';
  if (followers < 1000000) return 'Macro Creator';
  return 'Mega Creator';
}

function getEngagementStatus(
  engagementRate: number,
  platform: Platform
): 'excellent' | 'good' | 'average' | 'below-average' {
  const avg = PLATFORM_AVG_ENGAGEMENT[platform];
  if (engagementRate >= avg * 2) return 'excellent';
  if (engagementRate >= avg * 1.2) return 'good';
  if (engagementRate >= avg * 0.7) return 'average';
  return 'below-average';
}

export function calculateRate(input: RateInput): RateBreakdown {
  const { platform, followers, engagementRate, contentType, niche, usageRights, exclusivity } = input;

  const baseCPM = PLATFORM_CPM[platform];
  const baseRate = (followers / 1000) * baseCPM;

  const avgEngagement = PLATFORM_AVG_ENGAGEMENT[platform];
  const engagementRatio = engagementRate / avgEngagement;
  const engagementMultiplier = Math.max(0.5, Math.min(3.0, engagementRatio));

  const nichePremium = NICHE_PREMIUMS[niche];
  const contentTypeMultiplier = CONTENT_TYPE_MULTIPLIERS[contentType];

  const usageRightsAddon = usageRights ? 0.25 : 0;
  const exclusivityAddon = exclusivity ? 0.30 : 0;

  const rateAfterEngagement = baseRate * engagementMultiplier;
  const rateAfterContent = rateAfterEngagement * contentTypeMultiplier;
  const rateAfterNiche = rateAfterContent * (1 + nichePremium);
  const rateAfterAddons = rateAfterNiche * (1 + usageRightsAddon + exclusivityAddon);

  const finalRate = Math.max(50, Math.round(rateAfterAddons));
  const suggestedLow = Math.round(finalRate * 0.75);
  const suggestedMid = finalRate;
  const suggestedHigh = Math.round(finalRate * 1.35);

  const estimatedReach = Math.round(followers * (engagementRate / 100));

  return {
    baseCPM,
    baseRate: Math.round(baseRate),
    engagementMultiplier: Math.round(engagementMultiplier * 100) / 100,
    nichePremium,
    contentTypeMultiplier,
    usageRightsAddon,
    exclusivityAddon,
    finalRate,
    suggestedLow,
    suggestedMid,
    suggestedHigh,
    audienceTier: getAudienceTier(followers),
    engagementStatus: getEngagementStatus(engagementRate, platform),
    estimatedReach,
  };
}

export const NICHE_LABELS: Record<Niche, string> = {
  fashion: 'Fashion',
  beauty: 'Beauty',
  fitness: 'Fitness',
  food: 'Food & Cooking',
  travel: 'Travel',
  tech: 'Tech & Gadgets',
  finance: 'Finance',
  gaming: 'Gaming',
  lifestyle: 'Lifestyle',
  parenting: 'Parenting',
  education: 'Education',
  music: 'Music',
  sports: 'Sports',
  business: 'Business',
  health: 'Health & Wellness',
};

export const NICHE_PREMIUM_LABELS: Record<Niche, string> = {
  finance: '+45% premium',
  tech: '+40% premium',
  business: '+35% premium',
  health: '+30% premium',
  beauty: '+25% premium',
  fitness: '+20% premium',
  travel: '+20% premium',
  fashion: '+18% premium',
  education: '+15% premium',
  food: '+12% premium',
  sports: '+12% premium',
  gaming: '+10% premium',
  lifestyle: '+8% premium',
  parenting: '+8% premium',
  music: '+6% premium',
};

export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  post: 'Static Post',
  reel: 'Reel / Short-form',
  story: 'Story',
  video: 'Long-form Video',
  short: 'YouTube Short',
};

export const PLATFORM_CONTENT_TYPES: Record<Platform, ContentType[]> = {
  instagram: ['post', 'reel', 'story'],
  tiktok: ['reel', 'story'],
  youtube: ['video', 'short', 'story'],
};

export function formatCurrency(value: number): string {
  if (value >= 10000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  return `$${value.toLocaleString('en-US')}`;
}